import {
  isRangeDecimalVFrame,
  isRangeIntegerVFrame,
  isRangeMoneyDecimalVFrame,
  isRangeMoneyVFrame,
  type RangeFractionVFrame,
  type VFrameBase,
} from '@festina/life-general-types/life'
import { notEmpty, type Optional } from '@festina-life/core'
import {
  FormikDoubleInput,
  type FormikDoubleInputProps,
} from '@festina-life/ui/formikForms'
import get from 'lodash-es/get'
import { useMemo } from 'react'

import { useAmountFormatter } from '../../../hooks/useAmountFormatter'
import { isFieldRequired } from '../../forms/formUtility'
import { useLifeFormikContext } from '../../forms/useLifeFormikContext'
import {
  isDontUseVFrame,
  isFixedVFrame,
} from '../../forms/yup/validation/types'
import { isVFrameWithDefaultValue } from '../../forms/yup/validation/yupValidationUtils'
import { useFieldVFrame } from '../useFieldVFrame'
import type { InputPropsFromFormik } from './types'

type Props = InputPropsFromFormik<FormikDoubleInputProps>

type ValidationParams = {
  min: Optional<number>
  max: Optional<number>
  formatter: (value: Optional<string | number>) => string
}

/**
 *   double input used in life forms. This should be used in those cases where a frame exists to validate the input values.
 */
export const LifeDoubleInput = ({ name, ...props }: Props) => {
  const { values, setFieldValue } = useLifeFormikContext()
  const frameStatus = useFieldVFrame(name)
  const { formatWithDecimals, formatUnitlessWithDecimals, formatUnitless } =
    useAmountFormatter()

  const toValidationParams = useMemo(
    () =>
      (frameStatus: Optional<VFrameBase>): Optional<ValidationParams> => {
        if (!frameStatus) {
          return undefined
        }

        if (isRangeDecimalVFrame(frameStatus)) {
          return {
            min: frameStatus.min,
            max: frameStatus.max,
            formatter: formatUnitlessWithDecimals,
          }
        }

        if (isRangeIntegerVFrame(frameStatus)) {
          return {
            min: frameStatus.min,
            max: frameStatus.max,
            formatter: formatUnitless,
          }
        }

        if (isRangeMoneyDecimalVFrame(frameStatus)) {
          return {
            min: frameStatus.min,
            max: frameStatus.max,
            formatter: formatWithDecimals,
          }
        }

        if (isRangeMoneyVFrame(frameStatus)) {
          return {
            min: frameStatus.min,
            max: frameStatus.max,
            formatter: formatWithDecimals,
          }
        }

        return undefined
      },
    [formatUnitlessWithDecimals, formatUnitless, formatWithDecimals],
  )

  const formatValue = useMemo(
    () => (validationParams: Optional<ValidationParams>) => {
      if (!validationParams) {
        return undefined
      }

      const { min, max, formatter } = validationParams

      return notEmpty(min) || notEmpty(max)
        ? `${notEmpty(min) ? formatter(min) : ''}–${notEmpty(max) ? formatter(max) : ''}`
        : undefined
    },
    [],
  )

  if (isDontUseVFrame(frameStatus)) {
    return null
  }

  const fixed = isFixedVFrame(frameStatus)
  const isRequired = isFieldRequired(frameStatus)

  const value = get(values, name as string)

  const getValue = () => {
    if (fixed) {
      return frameStatus.value
    } else {
      return value
    }
  }

  const defaultValue = isVFrameWithDefaultValue(frameStatus)
    ? (frameStatus as RangeFractionVFrame)?.defaultValue
    : null

  return (
    <FormikDoubleInput
      name={name}
      editable={!fixed}
      defaultValue={defaultValue ?? undefined}
      validationText={formatValue(toValidationParams(frameStatus))}
      onDefaultValueClick={() => setFieldValue(name, defaultValue)}
      isRequired={isRequired}
      hasRightColumnAlways
      {...props}
      value={getValue()}
    />
  )
}
